/**
 * render_inline_panel confirmation chip.
 *
 * The AI's render_inline_panel tool pushes specs to the workspace Live
 * Canvas (docked side panel on the app), which updates live via
 * BrowserBus.
 *
 * Wherever a renderer slot shows this action (dashboard feed, AI pages)
 * it renders a thin confirmation chip: "Pushed [title] to Live Canvas →". Clicking it expands the
 * canvas if collapsed (best-effort via sessionStorage hint that
 * LiveCanvasPanel reads on mount).
 */
import { registerRenderer, type RendererProps } from "./registry.js";
import { ArrowRight, ArrowUpRight } from "lucide-react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";

export interface InlinePanelInput {
  /** PanelSpec — same vocabulary as compose_panel. Echoed back by the
   *  tool for audit/inspection, but NOT rendered here. */
  spec: unknown;
  /** Workspace this canvas push targeted. */
  workspace_id?: string | null;
  /** Optional one-line label. */
  title?: string | null;
  /** Live-canvas marker carried through from the tool response. */
  rendered?: string;
  /** Fallback URL for external MCP clients. */
  preview_url?: string;
}

function InlinePanelChip({ input }: RendererProps<InlinePanelInput>): JSX.Element {
  const title = input.title ?? "AI render";
  const previewUrl = input.preview_url;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-200 bg-white text-stone-800 text-xs shadow-card">
      <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
      <span className="font-semibold">Live Canvas:</span>
      <span className="truncate max-w-[280px]" title={title}>{title}</span>
      <button
        type="button"
        onClick={() => {
          // Hint to LiveCanvasPanel: a sessionStorage flag would only
          // take effect on remount, so we just dispatch a custom event
          // the panel can listen for if it wants to force-expand. Even
          // without that wiring, the panel auto-expands on update via
          // its useEffect on updated_at.
          window.dispatchEvent(new CustomEvent("cmcp:expand-live-canvas"));
        }}
        className="ml-1 text-action hover:text-action-hover font-semibold inline-flex items-center gap-0.5"
        title="Show the canvas"
      >
        Show <ArrowRight size={12} aria-hidden />
      </button>
      {previewUrl && (
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="text-stone-500 hover:text-action"
          title="Open as standalone preview"
          aria-label="Open as standalone preview"
        >
          <ArrowUpRight size={13} aria-hidden />
        </a>
      )}
    </div>
  );
}

registerRenderer<InlinePanelInput>({
  tool: "render_inline_panel",
  label: "Live Canvas push",
  Component: InlinePanelChip,
});
